import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { ADMIN_MANAGE_BUYER_COMPANY, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    AdminCreateBuyerImplementationRequest,
    CreateBuyerImplementationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-create-buyer/create-buyer-implementation';
import { ListAccountsService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-create-buyer/list-accounts';
import {
    EmptyRequest,
    ListPlatformsService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-create-buyer/list-platforms';
import {
    forLifeOf,
    getDynamicRouterLink,
    getRouterLinkById,
    IRadioOption,
    ISelectOption,
    LifecycleAware,
    LsqSearchComponent,
    LsqValidators,
    Regex
} from '@lsq-frontend/common-ui';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { PlatformId } from '@lsq-frontend/fasttrack-shared/app-constants';

enum AccountRelationshipType {
    GenerateAccount,
    ExistingAccount,
    NewAccount
}

@LifecycleAware()
@Component({
    selector: 'admin-create-buyer',
    styleUrls: ['./create-buyer.component.scss'],
    template: `
        <page-header
            titleText="Add New Buyer"
            backButtonText="back"
            (handleBackButtonClick)="navigateBack()"
        >
        </page-header>
        <div class="container-fluid page-container">
            <div class="row">
                <div class="col-12 col-lg-8">
                    <card [hasHeader]="false">
                        <div card-body [style.padding]="'3rem'">
                            <form
                                [formGroup]="formGroup"
                                (ngSubmit)="submitForm()"
                                [style.display]="platformSelectOptions ? 'block' : 'none'"
                            >
                                <div>
                                    <lsq-label
                                        name="companyName"
                                        label="Buyer Name*"
                                        subLabel="The name the buyer uses to do business."
                                    ></lsq-label>
                                    <label
                                        class="suffix"
                                        [attr.sandbox]="
                                            (isTestPlatform$ | async) ? sandboxSuffix : ''
                                        "
                                    >
                                        <lsq-input
                                            [style.marginBottom]="'3rem'"
                                            [basicLabel]="true"
                                            placeholder="Buyer Name"
                                            formControlName="companyName"
                                            [group]="formGroup"
                                        ></lsq-input>
                                    </label>

                                    <lsq-label
                                        name="legalName"
                                        label="Buyer Legal Name*"
                                        subLabel="The legal name of the buyer. May be the same as Buyer Name on many occasions."
                                    ></lsq-label>
                                    <label
                                        class="suffix"
                                        [attr.sandbox]="
                                            (isTestPlatform$ | async) ? sandboxSuffix : ''
                                        "
                                    >
                                        <lsq-input
                                            [style.marginBottom]="'3rem'"
                                            formControlName="legalName"
                                            [basicLabel]="true"
                                            placeholder="Legal Name"
                                            [group]="formGroup"
                                        ></lsq-input>
                                    </label>

                                    <lsq-label
                                        name="buyerExternalId"
                                        label="Buyer External ID*"
                                        subLabel="The ID used to identify this buyer."
                                    ></lsq-label>
                                    <label
                                        class="suffix"
                                        [attr.sandbox]="
                                            (isTestPlatform$ | async) ? sandboxSuffix : ''
                                        "
                                    >
                                        <lsq-input
                                            [style.marginBottom]="'3rem'"
                                            formControlName="buyerExternalId"
                                            [basicLabel]="true"
                                            placeholder="External ID"
                                            [group]="formGroup"
                                        ></lsq-input>
                                    </label>

                                    <div class="divider"></div>

                                    <lsq-label
                                        name="platformKey"
                                        label="Platform*"
                                        subLabel="Select the platform this buyer is associated with."
                                    ></lsq-label>
                                    <lsq-select
                                        *ngIf="platformSelectOptions"
                                        [style.marginBottom]="'3rem'"
                                        formControlName="platformKey"
                                        [basicLabel]="true"
                                        placeholder="Platform"
                                        [options]="platformSelectOptions"
                                        [group]="formGroup"
                                    ></lsq-select>

                                    <div class="divider"></div>

                                    <lsq-label
                                        name="Account Relationship"
                                        label="Account Relationship"
                                        subLabel="Select which method you will use to relate this new Buyer to an Account (a platform must be selected)."
                                    ></lsq-label>

                                    <create-buyer-account-relationship-radio-list
                                        [disabled]="!(this.selectedPlatformKey$ | async)"
                                        formControlName="accountRelationshipType"
                                    >
                                        <div
                                            account-search
                                            [style.margin-bottom]="'2.4rem'"
                                            [style.display]="
                                                (accountRelationshipType$ | async) ===
                                                accountRelationshipType.ExistingAccount
                                                    ? 'block'
                                                    : 'none'
                                            "
                                        >
                                            <lsq-search
                                                #accountSearch
                                                [style.display]="
                                                    selectedAccountKey ? 'none' : 'block'
                                                "
                                                #buyerSearch
                                                placeholder="Search Accounts"
                                                formControlName="accountSearchKey"
                                                [name]="'accountSearchKey'"
                                                [group]="formGroup"
                                                primaryDisplayKey="accountName"
                                                [primaryKey]="'accountKey'"
                                                [searchService]="listAccountsService"
                                                requestSearchTextFieldName="filter.accountName"
                                                [baseFilterRequest]="baseFilterRequest$ | async"
                                            >
                                            </lsq-search>
                                            <data-card *ngIf="selectedAccountKey">
                                                <p class="label">Account Relationship</p>
                                                <p class="body">{{ selectedAccountName }}</p>

                                                <lsq-button
                                                    appearance="transparent"
                                                    (buttonClick)="clearSelectedAccount()"
                                                >
                                                    Edit
                                                </lsq-button>
                                            </data-card>
                                        </div>

                                        <div
                                            new-account
                                            *ngIf="
                                                (accountRelationshipType$ | async) ===
                                                accountRelationshipType.NewAccount
                                            "
                                        >
                                            <lsq-label
                                                name="newAccountName"
                                                label="Account Name*"
                                                subLabel="The name of the new Account"
                                            ></lsq-label>
                                            <lsq-input
                                                [basicLabel]="true"
                                                formControlName="newAccountName"
                                                placeholder="Account Name"
                                                [group]="formGroup"
                                            ></lsq-input>
                                        </div>
                                    </create-buyer-account-relationship-radio-list>
                                </div>
                                <lsq-button
                                    [style.marginTop]="'3rem'"
                                    type="submit"
                                    [submitting]="isSubmitting"
                                >
                                    Create Buyer
                                </lsq-button>
                            </form>
                            <div *ngIf="!platformSelectOptions">
                                <lsq-circle-loader></lsq-circle-loader>
                            </div>
                        </div>
                    </card>
                </div>
                <div class="cols-12 col-lg-4">
                    <data-card [lightBorder]="true">
                        <p class="header" [style.marginBottom]="'1rem'">Additional Information</p>
                        <p class="body text-dark-gray">
                            This form will create a Buyer and associate it to an Account. The
                            remaining Buyer data can configured on the Buyer Management pages.
                        </p>
                    </data-card>
                </div>
            </div>
        </div>
    `
})
export class AdminCreateBuyerComponent implements OnInit, OnDestroy {
    @ViewChild('accountSearch') public accountSearch: LsqSearchComponent;
    public selectedAccountKey: string;
    public selectedAccountName: string;
    public isSubmitting = false;
    public sandboxSuffix = '- Sandbox';

    public formGroup = new FormGroup({
        accountRelationshipType: new FormControl(AccountRelationshipType.GenerateAccount),
        accountSearchKey: new FormControl(null),
        newAccountName: new FormControl(null, [Validators.maxLength(255)]),
        companyName: new FormControl(null, [
            Validators.required,
            LsqValidators.maxLength(255),
            Validators.pattern(Regex.NotSandbox)
        ]),
        legalName: new FormControl(null, [
            Validators.required,
            LsqValidators.maxLength(255),
            Validators.pattern(Regex.NotSandbox)
        ]),
        platformKey: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        buyerExternalId: new FormControl(null, [
            Validators.required,
            LsqValidators.maxLength(255),
            LsqValidators.pathSegment,
            Validators.pattern(Regex.NotSandbox)
        ])
    });
    public accountRelationshipType$: Observable<AccountRelationshipType> = this.formGroup
        .get('accountRelationshipType')
        .valueChanges.pipe(startWith(AccountRelationshipType.GenerateAccount));

    public selectedPlatformKey$ = this.formGroup.get('platformKey').valueChanges;

    public isTestPlatform$ = this.selectedPlatformKey$.pipe(
        map((selectedPlatformKey) => this.isTestPlatform(selectedPlatformKey))
    );

    public isTestPlatform(platformKey: string) {
        for (const platformOption of this.platformSelectOptions) {
            if (
                platformOption.value === platformKey &&
                platformOption.label === PlatformId.TEST_PLATFORM
            ) {
                return true;
            }
        }
        return false;
    }

    public baseFilterRequest$ = this.selectedPlatformKey$.pipe(
        map((selectedPlatformKey) => ({
            pagination: {
                skip: 0,
                take: 25
            },
            filter: {
                accountName: '',
                platformKey: selectedPlatformKey
            }
        })),
        startWith({
            pagination: {
                skip: 0,
                take: 25
            },
            filter: {
                accountName: '',
                platformKey: ''
            }
        })
    );

    public accountRelationshipType = AccountRelationshipType;
    public radioListOptions: IRadioOption[] = [
        {
            id: 'nonBusinessUnits',
            label: 'Automatically create an Account for this Buyer. (Use this for a Buyer that is not part of a Business Units Account)',
            value: AccountRelationshipType.GenerateAccount
        },
        {
            id: 'existingBusinessUnits',
            label: 'Relate this Buyer to an existing Account.',
            value: AccountRelationshipType.ExistingAccount
        },
        {
            id: 'newBusinessUnits',
            label: 'Create a new Account. (Use this if this is the first Buyer of a Business Units Account)',
            value: AccountRelationshipType.NewAccount
        }
    ];
    public platformSelectOptions: ISelectOption[];

    constructor(
        public listAccountsService: ListAccountsService,
        private _listPlatformsService: ListPlatformsService,
        private _createBuyerService: CreateBuyerImplementationService,
        private _router: Router,
        private _location: Location,
        private _errorToastBlacklistService: ErrorToastBlacklistService
    ) {
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            _createBuyerService.endpointName,
            400
        );
    }

    public async ngOnInit(): Promise<void> {
        const listPlatformsResponse = await this._listPlatformsService.executeOnce(
            new EmptyRequest()
        );

        // We only want to show D2B and CounselLink for now
        const platformsToShow = ['LSQ Enterprise', 'counselLink', 'esker', 'testPlatform'];
        this.platformSelectOptions = listPlatformsResponse.platforms
            .filter((platform) => platformsToShow.includes(platform.platformName))
            .map((platform) => ({
                value: platform.platformKey,
                label: platform.platformName,
                subLabel: `platformKey: ${platform.platformKey}`
            }));

        //If this fires reset the account
        this.selectedPlatformKey$.pipe(forLifeOf(this)).subscribe((_) => {
            this.selectedAccountKey = '';
            this.selectedAccountName = '';
            this.accountSearch.clearSearchAndValue();
            this.formGroup
                .get('accountRelationshipType')
                .reset(AccountRelationshipType.GenerateAccount);
            this.formGroup.get('accountSearchKey').reset();
        });

        this.formGroup
            .get('accountSearchKey')
            .valueChanges.pipe(forLifeOf(this))
            .subscribe((value) => {
                this.selectedAccountKey = value;
                this.selectedAccountName =
                    value !== null
                        ? this.accountSearch.getSelectedResultPrimaryDisplayText()
                        : null;
            });

        this.formGroup
            .get('accountRelationshipType')
            .valueChanges.pipe(forLifeOf(this))
            .subscribe((value) => {
                if (value === AccountRelationshipType.NewAccount) {
                    this.formGroup.get('accountSearchKey').clearValidators();
                    this.formGroup.get('accountSearchKey').reset();
                    this.formGroup
                        .get('newAccountName')
                        .setValidators([Validators.required, LsqValidators.maxLength(255)]);
                } else if (value === AccountRelationshipType.ExistingAccount) {
                    this.formGroup.get('newAccountName').reset();
                    this.formGroup.get('newAccountName').clearValidators();
                    this.formGroup
                        .get('accountSearchKey')
                        .setValidators([Validators.required, LsqValidators.maxLength(255)]);
                } else {
                    this.formGroup.get('newAccountName').reset();
                    this.formGroup.get('accountSearchKey').reset();
                    this.formGroup.get('newAccountName').clearValidators();
                    this.formGroup.get('accountSearchKey').clearValidators();
                }
                this.formGroup.get('newAccountName').updateValueAndValidity();
                this.formGroup.get('accountSearchKey').updateValueAndValidity();
            });
    }

    public async submitForm(): Promise<void> {
        this.formGroup.markAllAsTouched();

        if (this.formGroup.valid) {
            this.isSubmitting = true;

            const suffix = this.isTestPlatform(this.formGroup.value.platformKey)
                ? `${this.sandboxSuffix}`
                : '';
            const createBuyerRequest = new AdminCreateBuyerImplementationRequest({
                companyName: this.formGroup.value.companyName + suffix,
                legalName: this.formGroup.value.legalName + suffix,
                platformKey: this.formGroup.value.platformKey,
                accountKey: this.formGroup.value.accountSearchKey,
                accountName: this.formGroup.value.newAccountName,
                buyerExternalId: this.formGroup.value.buyerExternalId + suffix
            });

            try {
                const response = await this._createBuyerService.executeOnce(createBuyerRequest);

                const urlTree = this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, ADMIN_MANAGE_BUYER_COMPANY, {
                            noStandaloneLeadingSlash: true
                        }),
                        { buyerKey: response.buyerImplementationKey }
                    ),
                    {
                        queryParams: {
                            isImplementation: true
                        }
                    }
                );

                this._router.navigateByUrl(urlTree);
            } catch (error) {
                if (error.code === 400) {
                    this.formGroup.get('buyerExternalId').setErrors({
                        nonUnique: { message: 'Buyer External ID must be unique' }
                    });
                }
            } finally {
                this.isSubmitting = false;
            }
        }
    }

    public clearSelectedAccount(): void {
        this.selectedAccountKey = null;
        this.selectedAccountName = null;
        this.accountSearch.clearSearchAndValue();
    }

    public ngOnDestroy(): void {}

    public conditionalAccountNameRequiredValidator(formGroup: FormGroup) {
        if (formGroup.value.accountRelationshipType === AccountRelationshipType.NewAccount) {
            return Validators.required(formGroup.get('myEmailField'))
                ? {
                      myEmailFieldConditionallyRequired: true
                  }
                : null;
        }
        return null;
    }

    public navigateBack(): void {
        this._location.back();
    }
}
